import { Activity, BotMessageSquare, HeartPulse, FolderGit2, Gauge, Inbox, LayoutDashboard, Radar, ScrollText, Server, Settings, ShieldCheck, Spline, SquareTerminal, Target, Terminal, Wallet, Workflow } from '@orgus/ui/icons';
import type { ComponentType } from 'react';

export interface NavItem {
  /** Route path. */
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Second key of the g-sequence: g then this key jumps here. */
  key: string;
  /** Extra palette search words. */
  keywords: string[];
  /** Starts a labelled group in the rail. */
  section?: string;
}

/**
 * The left rail (ADR 0076): the ten daily destinations, always visible, in order. Routes and
 * the g-sequences come from these lists; the command palette reaches every item in all three.
 */
export const NAV: readonly NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, key: 'o', keywords: ['home', 'dashboard', 'needs you'] },
  { to: '/inbox', label: 'Inbox', icon: Inbox, key: 'n', keywords: ['approvals', 'needs you', 'questions', 'permissions', 'hitl'] },
  { to: '/assistant', label: 'Assistant', icon: BotMessageSquare, key: 'h', keywords: ['ai', 'agent', 'chat', 'ask', 'autonomy', 'copilot', 'automate'] },
  { to: '/missions', label: 'Missions', icon: Target, key: 'i', section: 'Delivery', keywords: ['backlog', 'items', 'features', 'epics', 'stories', 'defects', 'requirements'] },
  { to: '/sessions', label: 'Sessions', icon: SquareTerminal, key: 's', keywords: ['claude', 'transcripts'] },
  { to: '/agents', label: 'Agents & workflows', icon: Workflow, key: 'a', keywords: ['subagents', 'timeline', 'gantt'] },
  { to: '/pipelines', label: 'Pipelines', icon: Activity, key: 'p', keywords: ['landing', 'queue', 'deploy'] },
  { to: '/projects', label: 'Projects', icon: FolderGit2, key: 'r', section: 'Workspace', keywords: ['repositories', 'worktrees', 'checkouts'] },
  { to: '/machines', label: 'Machines', icon: Server, key: 'm', keywords: ['agents', 'hosts', 'capacity'] },
  { to: '/spend', label: 'Spend', icon: Wallet, key: 'c', keywords: ['cost', 'tokens', 'budget', 'money', 'finance', 'p&l', 'reconciliation', 'invoices', 'receipts', 'exports', 'accounting'] },
];

/** Pages opened now and then: folded under More at the foot of the rail (ADR 0076). */
export const MORE_NAV: readonly NavItem[] = [
  { to: '/audit', label: 'Audit', icon: ScrollText, key: 'u', keywords: ['log', 'history', 'actions'] },
  { to: '/capacity', label: 'Capacity', icon: Gauge, key: 'y', keywords: ['scheduler', 'queue', 'drain', 'labels', 'node pools', 'concurrency', 'placement'] },
  { to: '/flows', label: 'How Orgus works', icon: Spline, key: 'f', keywords: ['flows', 'runtime', 'autopilot', 'mission flow', 'canvas', 'workflow', 'graph', 'yaml'] },
  { to: '/terminals', label: 'Terminals', icon: Terminal, key: 't', keywords: ['shell', 'pty', 'take over', 'split view', 'attach'] },
  { to: '/security', label: 'Security', icon: Radar, key: 'e', keywords: ['alerts', 'kill switch', 'freeze', 'revoke', 'backup', 'restore', 'detection', 'incident'] },
  { to: '/health', label: 'Orgus health', icon: HeartPulse, key: 'l', keywords: ['loops', 'heartbeat', 'lander', 'follow-main', 'main check', 'autopilot', 'dead-man', 'watch'] },
  { to: '/discipline', label: 'Discipline', icon: ShieldCheck, key: 'd', keywords: ['rules', 'score', 'violations', 'house rules'] },
];

/** Settings: pinned in the rail footer, beside appearance and shortcuts. */
export const SETTINGS_NAV: NavItem = {
  to: '/settings',
  label: 'Settings',
  icon: Settings,
  key: ',',
  keywords: ['github', 'members', 'roles', 'policies', 'notifications', 'tokens', 'preferences', 'permissions', 'auto-approve', 'approval rules'],
};

/** Every page outside the ten daily destinations: More, then Settings. */
export const SECONDARY_NAV: readonly NavItem[] = [...MORE_NAV, SETTINGS_NAV];

/** True when `pathname` is `item`'s page or one of its sub-pages. */
export function isActive(item: NavItem, pathname: string): boolean {
  return item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function navForPath(pathname: string): NavItem | undefined {
  if (pathname === '/') return NAV[0];
  return [...NAV, ...SECONDARY_NAV].find((n) => n.to !== '/' && isActive(n, pathname));
}
